import { TRADE_STATUS_LABELS } from '@/types';
import type { DeliveryType, EffectiveTradeStatus, TradeStatus } from '@/types';

/**
 * Minimal structural shape needed to derive the effective status — satisfied by
 * both `TradeItem` (list/blotter) and `PositionDetail` (detail page).
 */
export interface EffectiveStatusInput {
  status: TradeStatus;
  delivery_type?: DeliveryType;
  cash_settled?: boolean;
  securities_settled?: boolean;
  pending_recall_quantity?: number;
}

/**
 * LR-497: derive a trade's effective settlement status.
 *
 * Two trader-facing statuses are not stored COLUMNS — they are computed per-row from
 * fields the API already returns:
 *
 *   • `partially_settled`          — an FOP opening (`pending_open_settlement`,
 *     `delivery_type === 'fop'`) where exactly ONE of the two legs has settled.
 *     Same condition the trades-list badge used for its former ½ glyph (LR-430).
 *   • `pending_partial_settlement` — an `active` trade carrying a pending partial
 *     recall/return (`pending_recall_quantity > 0`, LR-388/LR-470).
 *
 * Every other row maps straight through to its stored `status`, so each row has
 * exactly ONE effective status. The Status filter, table badge, detail-header
 * pill and applied-filter chips all key off this value, which is what keeps them
 * consistent with one another and with the sidebar options.
 *
 * THIS FUNCTION HAS SERVER TWINS: the `effective_status` annotation in
 * PositionViewSet.get_queryset() (LR-546) and `Position.is_partially_settled` (LR-608,
 * for paths holding un-annotated instances). Change the rule here and all three must
 * move together, or the blotter and the MTM run stop agreeing on what a row is.
 */
export function getEffectiveTradeStatus(item: EffectiveStatusInput): EffectiveTradeStatus {
  // Open-side only: closing goes `matured → closed` in one step and has no per-leg
  // settlement to derive from. A TD/SD close split would need per-leg close flags
  // first — see the note on Position.STATUS_CHOICES.
  if (
    item.status === 'pending_open_settlement' &&
    item.delivery_type === 'fop' &&
    !!item.cash_settled !== !!item.securities_settled
  ) {
    return 'partially_settled';
  }

  if (item.status === 'active' && (item.pending_recall_quantity ?? 0) > 0) {
    return 'pending_partial_settlement';
  }

  return item.status;
}

/**
 * LR-497: canonical display label for a trade's effective status.
 *
 * The SINGLE helper every status-rendering surface should call (the trades
 * badge, the detail-header pill via StatusPill, the FloatingActionBar chip, the
 * Terminate/Activity copy, plus the search basis), so the label a user
 * sees is identical everywhere and can never drift between surfaces.
 */
export function getEffectiveTradeStatusLabel(item: EffectiveStatusInput): string {
  return TRADE_STATUS_LABELS[getEffectiveTradeStatus(item)];
}
